var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css, ClassNames } from '@emotion/react';
import ReactModal from 'react-modal';
import { isFunction } from '../../util/type-check';
import { createUseModal, } from '../ModalContext';
import CloseButton from '../CloseButton';
import { StackContext } from '../StackContext';
import styled from '../../styles/styled';
import { AccessibilityError } from '../../util/errors';
const TRANSITION_DURATION_MOBILE = 120;
const TRANSITION_DURATION_DESKTOP = 240;
const TRANSITION_DURATION = Math.max(TRANSITION_DURATION_MOBILE, TRANSITION_DURATION_DESKTOP);
const closeButtonStyles = (theme) => css `
  position: absolute;
  top: ${theme.spacings.byte};
  right: ${theme.spacings.byte};
  z-index: ${theme.zIndex.absolute};
`;
const contentStyles = ({ theme }) => css `
  overflow-y: auto;

  ${theme.mq.untilKilo} {
    -webkit-overflow-scrolling: touch;
    padding: ${theme.spacings.mega};
    padding-bottom: calc(env(safe-area-inset-bottom) + ${theme.spacings.mega});
    width: 100vw;
  }

  ${theme.mq.kilo} {
    padding: ${theme.spacings.giga};
    padding-bottom: calc(env(safe-area-inset-bottom) + ${theme.spacings.giga});
    max-height: 90vh;
    min-width: 480px;
    max-width: 90vw;
  }
`;
const contentVariantStyles = ({ variant, theme, }) => {
    if (variant === 'contextual') {
        return css `
      ${theme.mq.untilKilo} {
        max-height: calc(100vh - ${theme.spacings.giga});
      }

      /* iOS viewport bug fix */
      /* https://allthingssmitty.com/2020/05/11/css-fix-for-100vh-in-mobile-webkit/ */
      @supports (max-height: -webkit-fill-available) {
        ${theme.mq.untilKilo} {
          max-height: 80vh;
        }
      }
    `;
    }
    if (variant === 'immersive') {
        return css `
      ${theme.mq.untilKilo} {
        height: 100%;
      }
    `;
    }
    return null;
};
const Content = styled.div(contentStyles, contentVariantStyles);
/**
 * The modal component displays self-contained tasks in a focused window that
 * overlays the page content.
 * Built on top of [`react-modal`](https://reactcommunity.org/react-modal/).
 */
export const Modal = (_a) => {
    var { children, onClose, variant = 'contextual', preventClose = false, closeButtonLabel, className } = _a, props = __rest(_a, ["children", "onClose", "variant", "preventClose", "closeButtonLabel", "className"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !preventClose &&
        !closeButtonLabel) {
        throw new AccessibilityError('Modal', "The `closeButtonLabel` prop is missing. Pass it in `setModal`, or pass `preventClose` if you intend to hide the Modal's close button.");
    }
    return (_jsx(ClassNames, { children: ({ css: cssString, cx, theme }) => {
            // React Modal styles
            // https://reactcommunity.org/react-modal/styles/classes/
            const styles = {
                base: cx(cssString `
              position: fixed;
              outline: none;
              background-color: var(--cui-bg-elevated);

              &::after {
                position: fixed;
                content: '';
                display: block;
                right: 0;
                bottom: 0;
                left: 0;
                background: linear-gradient(
                  rgba(255,255,255,0),
                  rgba(255,255,255,0.66),
                  rgba(255,255,255,1)
                );
              }

              ${theme.mq.untilKilo} {
                right: 0;
                bottom: 0;
                left: 0;
                transform: translateY(100%);
                transition: transform ${TRANSITION_DURATION_MOBILE}ms ease-in-out;

                &::after {
                  height: ${theme.spacings.mega};
                }
              }

              ${theme.mq.kilo} {
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                opacity: 0;
                transition: opacity ${TRANSITION_DURATION_DESKTOP}ms ease-in-out;
                border-radius: ${theme.borderRadius.mega};

                &::after {
                  height: ${theme.spacings.giga};
                  border-bottom-left-radius: ${theme.borderRadius.mega};
                  border-bottom-right-radius: ${theme.borderRadius.mega};
                }
              }
          `, variant === 'contextual' &&
                    cssString `
                ${theme.mq.untilKilo} {
                  border-top-left-radius: ${theme.borderRadius.mega};
                  border-top-right-radius: ${theme.borderRadius.mega};
                }
            `, variant === 'immersive' &&
                    cssString `
                top: 0;
            `),
                // The !important below is necessary because of some weird
                // style specificity issues in Emotion.
                afterOpen: cssString `
            ${theme.mq.untilKilo} {
              transform: translateY(0) !important;
            }

            ${theme.mq.kilo} {
              opacity: 1 !important;
            }
          `,
                beforeClose: cssString `
            ${theme.mq.untilKilo} {
              transform: translateY(100%) !important;
            }

            ${theme.mq.kilo} {
              opacity: 0 !important;
            }
        `,
            };
            const overlayStyles = {
                base: cssString `
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            opacity: 0;
            transition: opacity ${TRANSITION_DURATION_MOBILE}ms ease-in-out;
            background: var(--cui-bg-overlay);
            z-index: ${theme.zIndex.modal};

            ${theme.mq.kilo} {
              transition: opacity ${TRANSITION_DURATION_DESKTOP}ms ease-in-out;
            }
        `,
                afterOpen: cssString `
            opacity: 1;
        `,
                beforeClose: cssString `
            opacity: 0;
        `,
            };
            const reactModalProps = Object.assign({ className: styles, overlayClassName: overlayStyles, onRequestClose: onClose, closeTimeoutMS: TRANSITION_DURATION, shouldCloseOnOverlayClick: !preventClose, shouldCloseOnEsc: !preventClose, 
                /**
                 * react-modal relies on document.activeElement to return focus after the modal is closed.
                 * Safari and Firefox don't set it properly on button click (see https://github.com/reactjs/react-modal/issues/858 and https://github.com/reactjs/react-modal/issues/389).
                 * Returning the focus to document.body or to the focus-root can cause unwanted page scroll.
                 * Preventing scroll on focus would provide better UX for mouse users and shouldn't cause any side effects for assistive technology users.
                 */
                preventScroll: true }, props);
            return (_jsx(StackContext.Provider, Object.assign({ value: theme.zIndex.modal }, { children: _jsx(ReactModal, Object.assign({}, reactModalProps, { children: _jsxs(Content, Object.assign({ variant: variant, className: className }, { children: [!preventClose && closeButtonLabel && (_jsx(CloseButton, { onClick: onClose, label: closeButtonLabel, css: closeButtonStyles })), isFunction(children) ? children({ onClose }) : children] })) })) })));
        } }, variant));
};
Modal.TRANSITION_DURATION = TRANSITION_DURATION;
export const useModal = createUseModal(Modal);
